import React, { Fragment } from "react";
import * as palette from "@govlaunch/palette";
import Dotdotdot from "react-dotdotdot";
import { Toggle } from "react-powerplug";
import Popover from "@govlaunch/popover";
import Resource from "~/components/form/resources/Resource";
import RemoveResourceButton from "~/components/form/resources/RemoveResourceButton";
import ShowMoreButton from "~/components/form/resources/ShowMoreButton";
import { Anchor } from "@govlaunch/web";
import DocumentIcon from "~/components/icons/DocumentIcon";
import LinkIcon from "~/components/icons/LinkIcon";
import useShowMoreCounter from "~/lib/hooks/useShowMoreCounter";
import FontIcon from "~/components/icons/FontIcon";
import { IFlatResource, IDownloadResource, ILinkResource, ITextResource } from "~/types/types";

interface IResourcesProps {
  resources: IFlatResource[];
  onRemove: (resource: IFlatResource) => any;
}

export default function Resources({ resources, onRemove }: IResourcesProps) {
  const { offset, hasMore, showMore } = useShowMoreCounter(resources.length, 4);
  const resourcesSlice = resources.slice(0, offset);

  if (resourcesSlice.length === 0) {
    return null;
  }

  return (
    <>
      <div>
        {resourcesSlice.map((resource, index) => {
          return (
            // eslint-disable-next-line
            <Fragment key={index}>
              {resource.type === "download" && (
                <Anchor
                  css={{
                    fontSize: 14,
                    color: palette.darkGray,
                    textDecoration: "none",
                    fontWeight: 500,
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    "&:hover": {
                      color: palette.darkGray,
                    },
                    display: "block",
                    "&:focus, &:visited": {
                      boxShadow: "none",
                    },
                  }}
                  href={(resource as IDownloadResource).url || ""}
                  download={(resource as IDownloadResource).filename}
                  external="outside-govlaunch"
                >
                  <Resource
                    style={{
                      paddingRight: 30,
                    }}
                  >
                    <RemoveResourceButton
                      onClick={(event) => {
                        onRemove(resource);
                        event.preventDefault();
                      }}
                    />

                    <div
                      css={{
                        width: 24,
                      }}
                    >
                      <DocumentIcon
                        width={24}
                        height={24}
                        style={{
                          minWidth: 24,
                          minHeight: 24,
                        }}
                        fill="#28D094"
                      />
                    </div>

                    <div
                      css={{
                        marginLeft: 10,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
                    </div>
                  </Resource>
                </Anchor>
              )}

              {resource.type === "link" && (
                <Anchor
                  css={{
                    fontSize: 14,
                    color: palette.darkGray,
                    fontWeight: 500,
                    textDecoration: "none",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    "&:hover": {
                      color: palette.darkGray,
                    },
                    display: "block",
                    "&:focus, &:visited": {
                      boxShadow: "none",
                    },
                  }}
                  href={(resource as ILinkResource).url || ""}
                  external="outside-govlaunch"
                >
                  <Resource
                    style={{
                      paddingRight: 30,
                    }}
                  >
                    <RemoveResourceButton
                      onClick={(event) => {
                        onRemove(resource);
                        event.preventDefault();
                      }}
                    />

                    <LinkIcon
                      width={24}
                      height={24}
                      style={{
                        minWidth: 24,
                        minHeight: 24,
                      }}
                      fill="#28D094"
                    />

                    <div
                      css={{
                        marginLeft: 10,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
                    </div>
                  </Resource>
                </Anchor>
              )}

              {resource.type === "text" && (
                <Toggle>
                  {({ on, toggle }) => (
                    <Popover
                      placement="top"
                      isOpen={on}
                      onClickOutside={toggle}
                      boxStyle={{
                        boxShadow: "0 5px 30px 0 rgba(34, 35, 40, 0.2)",
                      }}
                      render={() => (
                        <div
                          css={{
                            padding: 15,
                            maxWidth: 320,
                            wordBreak: "break-all",
                            wordWrap: "break-word",
                          }}
                        >
                          {(resource as ITextResource).body}
                        </div>
                      )}
                    >
                      {({ innerRef }: { innerRef: any }) => (
                        <div ref={innerRef}>
                          <Resource
                            onClick={toggle}
                            css={{
                              paddingRight: 30,
                            }}
                          >
                            <RemoveResourceButton onClick={() => onRemove(resource)} />

                            <FontIcon
                              width={24}
                              height={24}
                              style={{
                                minWidth: 24,
                                minHeight: 24,
                              }}
                              fill="#28D094"
                            />

                            <div
                              css={{
                                marginLeft: 10,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <span
                                css={{
                                  fontSize: 14,
                                  color: palette.darkGray,
                                  lineHeight: 1.2,
                                  fontWeight: 500,
                                  wordBreak: "break-word",
                                }}
                              >
                                <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
                              </span>
                            </div>
                          </Resource>
                        </div>
                      )}
                    </Popover>
                  )}
                </Toggle>
              )}
            </Fragment>
          );
        })}
      </div>

      {hasMore && <ShowMoreButton onClick={showMore} />}
    </>
  );
}
